import type { DefaultError, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../clients';
import { queryKeys } from '../lib/query-keys';
import {
  getClustersBasePath,
  getClustersForSchoolPath,
  getClustersPublishPath,
  getClustersV2Path,
} from '../utils/api-paths';
import {
  enqueueUpdateStudentPlansForPathways,
  extractPathwayIdsFromCluster,
  type ClusterPathwayRef,
} from '../utils/cluster-publish-and-update';
import {
  getCpContextHighschoolId,
  getHighschoolId,
  isClustersAndPathwaysWafDescriptionEncodingEnabled,
  isConsolidatedPermissionsEnabled,
  isPosReadOnly,
} from '../utils/cp-context';
import { encodeWafSafeDescriptionsInRequestBody } from '../utils/waf-safe-description';
import type {
  ClusterListItem,
  ClustersListBody,
  ClustersListFilters,
  ClustersListResponse,
} from '../types/clusters';
import clustersListMock from '../mock-data/clusters-list.json';
import clustersForSchoolMock from '../mock-data/clusters-by-school.json';

export interface CreateClusterPayload {
  name: string;
  description: string;
}

/** Full cluster JSON for publish (legacy CPUI `PUT /clusters/publish`). */
export type ClusterDocument = Record<string, unknown>;

export type PublishClusterVariables = {
  body: ClusterDocument;
  /** Schools selected in the publish drawer — used to enqueue student plan propagation jobs. */
  checkedSchoolNids: string[];
  /** Pathways (specializations) under the area; when omitted, no student propagation is enqueued. */
  pathways?: ClusterPathwayRef[];
};

export type PublishClusterResult = {
  publishData: unknown;
  /** Count of `update-pos-student-plans` jobs enqueued (one per pathway). */
  studentPlanUpdatesQueued: number;
};

export type UpdateClusterPayload = ClusterDocument &
  CreateClusterPayload & {
    id: string;
    descriptionPlainText?: string;
  };

function normalizeClusterUpdatePayload(payload: UpdateClusterPayload): UpdateClusterPayload {
  const normalized: UpdateClusterPayload = { ...payload };

  delete normalized.pathways;

  return normalized;
}

function isMockMode(): boolean {
  return Boolean(import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true');
}

function unwrapClusterBody(body: unknown): unknown {
  let current: unknown = body;
  for (let depth = 0; depth < 6; depth++) {
    if (!current || typeof current !== 'object') {
      return current;
    }
    const r = current as Record<string, unknown>;
    const idLike = String(r.id ?? '').trim();
    if (idLike.length > 0) {
      return current;
    }
    const next = r.data ?? r.result ?? r.cluster;
    if (next === undefined || next === null || next === current) {
      return current;
    }
    current = next;
  }
  return current;
}

function joinPathwayNames(pathways: Array<{ name?: string }> | undefined): string {
  if (!pathways?.length) {
    return '';
  }
  return pathways
    .map((p) => p.name?.trim())
    .filter(Boolean)
    .join('; ');
}

function normalizeClusterSearch(search: string | undefined): string {
  return search?.trim().toLowerCase() ?? '';
}

/**
 * Returns whether a specialization area row matches a free-text search.
 *
 * @param cluster - Area row with its area name and nested specialization names
 * @param search - User-entered text to match case-insensitively and partially
 * @returns True when the area name or any specialization name contains the search term
 */
export function matchesClusterSearch(
  cluster: Pick<ClusterListItem, 'name' | 'specializationSummary' | 'pathways'>,
  search: string | undefined
): boolean {
  const normalizedSearch = normalizeClusterSearch(search);
  if (!normalizedSearch) {
    return true;
  }

  if (cluster.name.toLowerCase().includes(normalizedSearch)) {
    return true;
  }

  if (cluster.specializationSummary.toLowerCase().includes(normalizedSearch)) {
    return true;
  }

  return (cluster.pathways ?? []).some((pathway) =>
    pathway.name?.trim().toLowerCase().includes(normalizedSearch)
  );
}

function transformCluster(raw: {
  id?: string;
  name?: string;
  description?: string;
  lastPublished?: number | null;
  pathways?: Array<{
    id?: string;
    clusterId?: string;
    name?: string;
    totalCredits?: number;
    description?: string;
    publishedSchools?: Array<{ nid?: string }>;
    activeSchools?: Array<{ nid?: string }>;
    requirements?: unknown[];
    classYearFrom?: number;
    classYearTo?: number;
    type?: number;
  }>;
  publishedSchools?: Array<{ nid?: string }>;
}): ClusterListItem {
  const pathways = Array.isArray(raw.pathways) ? raw.pathways : [];
  const description = typeof raw.description === 'string' ? raw.description : undefined;
  return {
    id: raw.id ?? '',
    name: raw.name ?? '',
    description,
    lastPublished: typeof raw.lastPublished === 'number' ? raw.lastPublished : null,
    pathways,
    publishedSchools: Array.isArray(raw.publishedSchools) ? raw.publishedSchools : [],
    specializationSummary: joinPathwayNames(pathways),
  };
}

/** Mock-only: simulate school + "has pathways" when using `clusters-list.json` (no HTTP). */
export function useGetClusterById(id: string): UseQueryResult<ClusterListItem, DefaultError> {
  const apiClient = useApiClient();
  const path = getClustersBasePath();

  return useQuery({
    queryKey: queryKeys.clusters.detail(id),
    enabled: Boolean(id.trim()),
    queryFn: async (): Promise<ClusterListItem> => {
      if (isMockMode()) {
        const body = clustersListMock as {
          data?: Array<Parameters<typeof transformCluster>[0]>;
        };
        const trimmedId = id.trim();
        const raw = (body.data ?? []).find((c) => String(c.id ?? '').trim() === trimmedId);
        if (raw) {
          return transformCluster(raw);
        }
        return transformCluster({
          id: trimmedId,
          name: trimmedId,
          pathways: [],
          publishedSchools: [],
        });
      }
      const response = await apiClient.get(`${path}/${encodeURIComponent(id)}`);
      const body = response.data;
      const raw = (body?.data ?? body) as Parameters<typeof transformCluster>[0];
      return transformCluster(raw);
    },
    retry: false,
    throwOnError: false,
  });
}

/** Mock-only: simulate school + “has pathways” when using `clusters-list.json` (no HTTP). */
function applyClusterListFiltersMock(
  list: ClusterListItem[],
  filters?: ClustersListFilters
): ClusterListItem[] {
  let result = list;
  if (filters?.hideNoActiveSpecializations) {
    result = result.filter((c) => (c.pathways?.length ?? 0) > 0);
  }
  const schoolNids = resolveClusterSchoolNids(filters);
  if (schoolNids.length > 0) {
    const selectedSchools = new Set(schoolNids);
    result = result.filter((c) => c.publishedSchools.some((s) => selectedSchools.has(s.nid ?? '')));
  }
  return result;
}

/**
 * Resolves the effective school NIDs for cluster v2 requests.
 *
 * Flag OFF: legacy {@link isPosReadOnly} / {@link getHighschoolId} scoping.
 * Flag ON: district manage users see all areas (`[]`); view-only users scope to embedded school
 * when present, otherwise district-wide read (`[]`) — aligned with
 * {@link resolveSchoolNidsForPlansOfStudyList} in plans-of-study-repository.
 *
 * @param filters - Optional cluster list filters from the UI
 * @returns School NIDs for the POST body `schools` field
 */
export function resolveClusterSchoolNids(filters?: ClustersListFilters): string[] {
  const selectedSchools = (filters?.schools ?? []).map((nid) => nid.trim()).filter(Boolean);
  if (selectedSchools.length > 0) {
    return selectedSchools;
  }

  if (!isConsolidatedPermissionsEnabled()) {
    if (!isPosReadOnly()) {
      return selectedSchools;
    }

    const schoolId = getHighschoolId().trim();
    return schoolId.length > 0 ? [schoolId] : [];
  }

  if (!isPosReadOnly()) {
    return [];
  }

  const schoolId = getCpContextHighschoolId()?.trim() ?? '';
  return schoolId.length > 0 ? [schoolId] : [];
}

function buildClustersPostBody(filters?: ClustersListFilters): ClustersListBody {
  const search = filters?.search?.trim();
  return {
    schools: resolveClusterSchoolNids(filters),
    ...(search ? { search } : {}),
    ...(filters?.hideNoActiveSpecializations ? { status: 'ACTIVE' } : {}),
    page: filters?.page ?? 1,
    pageSize: filters?.pageSize ?? 20,
  };
}

/**
 * Paginated clusters list (Specialization areas).
 *
 * **Non-mock — `GET` query string (examples):**
 * - `GET /course-planner/clusters?page=1&pageSize=20&school=889727USPR` (Succeed / embedded)
 * - `GET /course-planner/clusters?page=1&pageSize=20&school=889727USPR&status=ACTIVE` when "Hide areas with no active specializations" is on (default)
 * - Direct local service: `GET /clusters/district/{d}/highschool/{h}?...` (same params; see `getClustersBasePath()`).
 *
 * **Params always sent:** `page`, `pageSize`. **Optional:** `search`, `school` (when non-empty).
 * **When `hideNoActiveSpecializations` is true (default):** also **`status=ACTIVE`**. When false, `status` is omitted.
 *
 * **Mock** (`VITE_USE_MOCKS=true` in dev): no HTTP; fixture + `applyClusterListFiltersMock` before pagination.
 */
export function useGetClusters(
  filters?: ClustersListFilters,
  enabled = true
): UseQueryResult<ClustersListResponse, DefaultError> {
  const apiClient = useApiClient();
  const v2Path = getClustersV2Path();

  return useQuery({
    queryKey: queryKeys.clusters.list(filters ?? {}),
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<ClustersListResponse> => {
      if (isMockMode()) {
        const body = clustersListMock as {
          data: Array<{
            id?: string;
            name?: string;
            description?: string;
            pathways?: Array<{ name?: string }>;
            publishedSchools?: Array<{ nid?: string }>;
          }>;
          total: number;
          currentPage: number;
          totalPages: number;
          pageSize: number;
        };
        let list = (body.data ?? []).map(transformCluster);
        if (normalizeClusterSearch(filters?.search)) {
          list = list.filter((cluster) => matchesClusterSearch(cluster, filters?.search));
        }
        list = applyClusterListFiltersMock(list, filters);
        const page = filters?.page ?? 1;
        const pageSize = filters?.pageSize ?? 20;
        const total = list.length;
        const totalPages = Math.max(1, Math.ceil(total / pageSize));
        const start = (page - 1) * pageSize;
        const data = list.slice(start, start + pageSize);
        return { data, total, currentPage: page, totalPages, pageSize };
      }

      const response = await apiClient.post(v2Path, buildClustersPostBody(filters));
      const body = response.data;
      const rawList: unknown[] = Array.isArray(body?.data) ? body.data : [];
      const data = rawList.map((row) =>
        transformCluster(row as Parameters<typeof transformCluster>[0])
      );
      const total = typeof body?.total === 'number' ? body.total : data.length;
      const currentPage = body?.currentPage ?? filters?.page ?? 1;
      const pageSize = body?.pageSize ?? filters?.pageSize ?? 20;
      const totalPages =
        typeof body?.totalPages === 'number'
          ? body.totalPages
          : Math.max(1, Math.ceil(total / pageSize));

      return { data, total, currentPage, totalPages, pageSize };
    },
    retry: false,
    throwOnError: false,
  });
}

/** A single Specialization (Pathway) nested inside an Area (Cluster). */
export interface ClusterPathway {
  id: string;
  name: string;
  clusterId?: string;
  classYearFrom?: number;
  classYearTo?: number;
}

/** An Area (Cluster) with its nested active Specializations (Pathways). */
export interface ClusterWithPathways {
  id: string;
  name: string;
  pathways: ClusterPathway[];
}

/**
 * Fetch Areas (Clusters) with active Specializations (Pathways) published to a school.
 * Calls GET /clusters/findByActiveSchool?activeSchool={schoolId}&pageSize=999.
 *
 * Only fetches when schoolId is non-empty.
 */
export function useGetClustersForSchool(
  schoolId: string | undefined,
  enabled = true
): UseQueryResult<ClusterWithPathways[], DefaultError> {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.clusters.bySchool(schoolId ?? ''),
    queryFn: async () => {
      if (isMockMode()) {
        return clustersForSchoolMock as ClusterWithPathways[];
      }
      const path = getClustersForSchoolPath(schoolId ?? '');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await apiClient.get<any>(path);
      const raw = response.data;
      // CPS returns PaginatedResult<ClusterModel> → { data: ClusterModel[], total, ... }
      // Extract the nested .data array; fall back gracefully for any other shape.
      if (Array.isArray(raw)) {
        return raw as ClusterWithPathways[];
      }
      if (raw && Array.isArray(raw.data)) {
        return raw.data as ClusterWithPathways[];
      }
      return [] as ClusterWithPathways[];
    },
    enabled: Boolean(schoolId) && enabled,
  });
}

export function useCreateCluster(): UseMutationResult<
  ClusterListItem,
  DefaultError,
  CreateClusterPayload
> {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const path = getClustersBasePath();

  return useMutation({
    mutationFn: async (payload: CreateClusterPayload): Promise<ClusterListItem> => {
      const body = isClustersAndPathwaysWafDescriptionEncodingEnabled()
        ? encodeWafSafeDescriptionsInRequestBody(payload)
        : payload;
      if (isMockMode()) {
        return {
          id: String(Date.now()),
          name: payload.name,
          description: payload.description,
          pathways: [],
          publishedSchools: [],
          specializationSummary: '',
        };
      }
      const response = await apiClient.post(path, body);
      const responseBody = response.data;
      const raw = (responseBody?.data ?? responseBody) as Parameters<typeof transformCluster>[0];
      return transformCluster(raw);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clusters.lists() });
    },
  });
}

export function useUpdateCluster(): UseMutationResult<
  ClusterListItem,
  DefaultError,
  UpdateClusterPayload
> {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const path = getClustersBasePath();

  return useMutation({
    mutationFn: async (payload: UpdateClusterPayload): Promise<ClusterListItem> => {
      const normalized = normalizeClusterUpdatePayload(payload);
      const body = isClustersAndPathwaysWafDescriptionEncodingEnabled()
        ? encodeWafSafeDescriptionsInRequestBody(normalized)
        : normalized;

      if (isMockMode()) {
        return {
          id: payload.id,
          name: payload.name,
          description: payload.description,
          pathways: [],
          publishedSchools: [],
          specializationSummary: '',
        };
      }

      const response = await apiClient.put(path, body);
      const raw = unwrapClusterBody(response.data) as Parameters<typeof transformCluster>[0];
      return transformCluster(raw);
    },
    onSuccess: (_cluster) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clusters.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.clusters.detail(_cluster.id) });
    },
  });
}

/**
 * Full cluster model for publish (same GET as detail; raw shape for `PUT /clusters/publish`).
 */
export function useClusterDocumentById(
  clusterId: string,
  enabled = true
): UseQueryResult<ClusterDocument, DefaultError> {
  const apiClient = useApiClient();
  const path = getClustersBasePath();

  return useQuery({
    queryKey: queryKeys.clusters.document(clusterId),
    enabled: Boolean(clusterId.trim()) && enabled,
    queryFn: async (): Promise<ClusterDocument> => {
      if (isMockMode()) {
        const body = clustersListMock as { data?: Array<Record<string, unknown>> };
        const row = (body.data ?? []).find((c) => String(c.id ?? '').trim() === clusterId.trim());
        if (row) {
          return { ...row } as ClusterDocument;
        }
        return {
          id: clusterId,
          name: 'Area',
          pathways: [],
          publishedSchools: [],
          activeSchools: [],
        } as ClusterDocument;
      }
      const response = await apiClient.get(`${path}/${encodeURIComponent(clusterId)}`);
      const raw = unwrapClusterBody(response.data) as ClusterDocument;
      return raw && typeof raw === 'object' ? raw : ({} as ClusterDocument);
    },
    retry: false,
    throwOnError: false,
  });
}

/**
 * Publish cluster to schools (`PUT /course-planner/clusters/publish`), then enqueue
 * `update-pos-student-plans` per pathway so student plans pick up published specialization changes.
 * Body must omit `pathways` (legacy CPUI strips pathways before send).
 */
export function usePublishCluster(): UseMutationResult<
  PublishClusterResult,
  DefaultError,
  PublishClusterVariables
> {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const publishPath = getClustersPublishPath();

  return useMutation({
    mutationFn: async ({
      body,
      checkedSchoolNids,
      pathways,
    }: PublishClusterVariables): Promise<PublishClusterResult> => {
      const payload: ClusterDocument = { ...body };
      delete payload.pathways;
      const requestBody = isClustersAndPathwaysWafDescriptionEncodingEnabled()
        ? encodeWafSafeDescriptionsInRequestBody(payload)
        : payload;
      if (isMockMode()) {
        const pathwayIds = extractPathwayIdsFromCluster(pathways);
        return {
          publishData: requestBody,
          studentPlanUpdatesQueued: pathwayIds.length,
        };
      }
      const response = await apiClient.put<unknown>(publishPath, requestBody);
      const pathwayIds = extractPathwayIdsFromCluster(pathways);
      const studentPlanUpdatesQueued = await enqueueUpdateStudentPlansForPathways(apiClient, {
        pathwayIds,
        pathways,
        checkedSchoolNids,
      });
      return { publishData: response.data, studentPlanUpdatesQueued };
    },
    onSuccess: (result, variables) => {
      const id = String(variables.body.id ?? '').trim();
      if (id.length > 0) {
        const raw = unwrapClusterBody(result.publishData);
        const nextDocument =
          raw && typeof raw === 'object' && String((raw as { id?: string }).id ?? '').trim()
            ? (raw as ClusterDocument)
            : ({ ...variables.body } as ClusterDocument);

        queryClient.setQueryData(queryKeys.clusters.document(id), nextDocument);
        queryClient.setQueryData(
          queryKeys.clusters.detail(id),
          transformCluster(nextDocument as Parameters<typeof transformCluster>[0])
        );
        void queryClient.invalidateQueries({ queryKey: queryKeys.clusters.document(id) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.clusters.detail(id) });
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.clusters.lists() });
    },
  });
}
